import { BackendModule } from 'i18next';
import { returnVoid } from '../../util/returnVoid';
import { SsrData } from '../SsrData';
import { Translation } from './constants';
import { loadTranslationAsync } from './loadTranslationAsync';
import { loadTranslationSync } from './loadTranslationSync.server-only';

/** A custom translation-loading backend for i18next which:
 * - always loads English synchronously
 * - uses an injected SSR cache, and tries to load from that first
 * - loads translations synchronously when on the server
 * - loads translations asynchronously on the client
 */
export class SsrBackend implements BackendModule {
  constructor(private ssrData: SsrData) {}

  slot: Cache = this.ssrData.slot('TRANSLATIONS', () => ({}));
  type = 'backend' as const;
  create = returnVoid;
  init = returnVoid;
  read: BackendModule['read'] = (language, namespace, callback) => {
    const cacheKey = `${language}/${namespace}`;
    const cached = this.slot[cacheKey];

    /** Any language which has been loaded previously (on the client or during SSR) is synchronously available */
    if (cached) {
      callback(null, cached);
      return;
    }

    /** On the server, all languages are synchronously available and are stored in the cache upon use */
    if (loadTranslationSync) {
      try {
        const translation = loadTranslationSync(language, namespace);
        this.slot[cacheKey] = translation;
        callback(null, translation);
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        callback(error, false);
      }

      return;
    }

    /** In all other cases, the language is loaded asynchronously and stored in the cache */
    loadTranslationAsync(language, namespace).then(
      translation => {
        this.slot[cacheKey] = translation;
        return callback(null, translation);
      },
      error => callback(error, false),
    );
  };
}

type Cache = Record<string, Translation>;
